import React from 'react';
import { Shield, Lock, Copyright, AlertTriangle } from 'lucide-react';

// Legal Footer Component - Add to bottom of every page
export const LegalFooter = ({ className = "" }) => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className={`bg-slate-900 border-t border-slate-700 py-6 mt-auto ${className}`}>
      <div className="max-w-7xl mx-auto px-4">
        {/* Legal Notice */}
        <div className="flex items-start gap-3 bg-slate-800/50 rounded-lg p-4 border border-slate-700/50 mb-4">
          <Shield className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-gray-400 leading-relaxed">
            <p className="font-bold text-gray-300 mb-1">PROPRIETARY AND CONFIDENTIAL</p>
            <p>
              This website, including all content, features, functionality, design, graphics, and underlying software 
              (collectively, the "Platform"), is the exclusive proprietary property of Baselinx PLM and is protected by 
              United States and international copyright, trademark, patent, trade secret, and other intellectual property 
              or proprietary rights laws.
            </p>
          </div>
        </div>

        {/* Restrictions Notice */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Lock className="w-4 h-4 text-red-400" />
            <span>Unauthorized reproduction prohibited</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <AlertTriangle className="w-4 h-4 text-yellow-400" />
            <span>Distribution strictly forbidden</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Shield className="w-4 h-4 text-cyan-400" />
            <span>All rights reserved worldwide</span>
          </div>
        </div>

        {/* Legal Text */}
        <div className="text-[10px] text-gray-500 leading-relaxed border-t border-slate-700/50 pt-4">
          <p className="mb-2">
            <strong>NOTICE:</strong> This Platform may not be copied, reproduced, distributed, transmitted, displayed, 
            published, adapted, modified, sold, licensed, or otherwise exploited for any purpose whatsoever without the 
            prior written consent of Baselinx PLM. Any unauthorized use, reproduction, or distribution of this Platform 
            or any portion thereof may result in severe civil and criminal penalties, and will be prosecuted to the 
            maximum extent possible under the law.
          </p>
          <p className="mb-2">
            The information contained in this Platform is provided for demonstration purposes only. All data, names, 
            and scenarios are fictional and do not represent actual programs, contracts, or governmental operations. 
            Any resemblance to actual events or persons is purely coincidental.
          </p>
          <p>
            By accessing or using this Platform, you agree to be bound by these terms. If you do not agree to these 
            terms, you are not authorized to access or use this Platform.
          </p>
        </div>

        {/* Copyright */}
        <div className="flex flex-col items-center gap-3 mt-4 pt-4 border-t border-slate-700/50">
          {/* Policy Links */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
            <a href="/cyber-policy" className="text-cyan-400 hover:text-cyan-300 transition">Cybersecurity Policy</a>
            <span className="text-gray-600">|</span>
            <a href="/nist-compliance" className="text-cyan-400 hover:text-cyan-300 transition">NIST 800-171 Compliance</a>
            <span className="text-gray-600">|</span>
            <a href="/data-handling" className="text-cyan-400 hover:text-cyan-300 transition">Data Handling Statement</a>
            <span className="text-gray-600">|</span>
            <a href="/legal/privacy" className="text-cyan-400 hover:text-cyan-300 transition">Privacy Policy</a>
          </div>
          
          <div className="flex items-center gap-2">
            <Copyright className="w-4 h-4 text-gray-500" />
            <span className="text-xs text-gray-500">
              {currentYear} Baselinx PLM. All Rights Reserved. Patent Pending. ITAR/EAR Compliant.
            </span>
          </div>
          
          {/* Federal Registration Info */}
          <div className="flex flex-wrap items-center justify-center gap-3 text-[10px] text-gray-600">
            <span>D-U-N-S®: <span className="text-cyan-500 font-mono">14-392-8991</span></span>
            <span className="text-gray-700">|</span>
            <span>UEI: <span className="text-cyan-500 font-mono">GEAVTX6X2685</span></span>
            <span className="text-gray-700">|</span>
            <span>CAGE: <span className="text-cyan-500 font-mono">1A2G6</span></span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Legal Banner Component - Show before login
export const LegalBanner = ({ onAccept, onDecline }) => {
  return (
    <div className="fixed inset-0 bg-slate-900/95 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 rounded-2xl border border-slate-700 max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-900/50 to-slate-800 p-6 border-b border-slate-700 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-500/20 rounded-xl">
              <Shield className="w-8 h-8 text-red-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Legal Notice & Terms of Use</h1>
              <p className="text-gray-400 text-sm">Please read carefully before proceeding</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* DEMO / FICTIONAL DATA NOTICE */}
          <div className="bg-blue-500/10 border-2 border-blue-500/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-bold text-blue-400">DEMONSTRATION PURPOSE ONLY</h2>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              <strong className="text-blue-400">ALL DATA DISPLAYED ON THIS PLATFORM IS ENTIRELY FICTIONAL.</strong> This 
              website is a demonstration prototype intended solely for evaluation and presentation purposes. All names, 
              part numbers, programs, contracts, organizations, and scenarios depicted are simulated and do not represent 
              any actual government programs, defense contracts, or real-world operations. Any resemblance to actual 
              events, entities, or persons is purely coincidental.
            </p>
          </div>

          {/* Proprietary Notice */}
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-5 h-5 text-red-400" />
              <h2 className="text-lg font-bold text-red-400">PROPRIETARY AND CONFIDENTIAL</h2>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              This website and all associated content, including but not limited to software, documentation, 
              graphics, user interfaces, workflows, and data structures (collectively, the "Platform") constitute 
              proprietary trade secrets and confidential information owned exclusively by Baselinx PLM.
            </p>
          </div>

          {/* Intellectual Property */}
          <div className="bg-slate-700/30 rounded-xl p-4">
            <h3 className="text-white font-bold mb-2 flex items-center gap-2">
              <Copyright className="w-4 h-4 text-cyan-400" />
              Intellectual Property Rights
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              The Platform is protected by United States and international intellectual property laws, including 
              but not limited to copyright, trademark, patent, and trade secret laws. All rights, title, and 
              interest in and to the Platform, including all intellectual property rights therein, are and shall 
              remain the exclusive property of Baselinx PLM.
            </p>
          </div>

          {/* Prohibited Activities */}
          <div className="bg-slate-700/30 rounded-xl p-4">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              Strictly Prohibited Activities
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span className="text-gray-400">Copying or reproducing any part of the Platform</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span className="text-gray-400">Distributing or sharing access credentials</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span className="text-gray-400">Reverse engineering or decompiling software</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span className="text-gray-400">Creating derivative works without authorization</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span className="text-gray-400">Using for competitive analysis or benchmarking</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span className="text-gray-400">Removing or altering proprietary notices</span>
              </div>
            </div>
          </div>

          {/* Legal Consequences */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
            <h3 className="text-yellow-400 font-bold mb-2">Legal Consequences</h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              Unauthorized access, use, reproduction, or distribution of this Platform or any portion thereof 
              may result in severe civil and criminal penalties. Violators will be prosecuted to the fullest 
              extent of the law, including but not limited to claims for injunctive relief, actual damages, 
              statutory damages, attorneys' fees, and criminal prosecution where applicable.
            </p>
          </div>

          {/* Acknowledgment */}
          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
            <h3 className="text-cyan-400 font-bold mb-2">Acknowledgment</h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              By clicking "I Accept" below, you acknowledge that you have read, understood, and agree to be 
              bound by these terms. You further acknowledge that this Platform is proprietary and confidential, 
              and that you will not use it for any purpose other than its intended demonstration purpose.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-slate-700 bg-slate-800/50 rounded-b-2xl">
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            {onDecline && (
              <button
                onClick={onDecline}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-gray-300 rounded-lg font-bold transition"
              >
                Decline & Exit
              </button>
            )}
            <button
              onClick={onAccept}
              className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-bold transition flex items-center justify-center gap-2"
            >
              <Shield className="w-4 h-4" />
              I Accept These Terms
            </button>
          </div>
          <p className="text-center text-xs text-gray-500 mt-3">
            Your acceptance is logged for compliance purposes.
          </p>
          
          {/* Federal Registration Info */}
          <div className="mt-4 pt-4 border-t border-slate-700/50">
            <p className="text-center text-[10px] text-gray-600 mb-2">Baselinx PLM LLC — Registered Federal Contractor</p>
            <div className="flex flex-wrap items-center justify-center gap-3 text-[10px] text-gray-500">
              <span>D-U-N-S®: <span className="text-cyan-500 font-mono">14-392-8991</span></span>
              <span className="text-gray-700">|</span>
              <span>UEI: <span className="text-cyan-500 font-mono">GEAVTX6X2685</span></span>
              <span className="text-gray-700">|</span>
              <span>CAGE: <span className="text-cyan-500 font-mono">1A2G6</span></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Compact Legal Notice for inline use
export const CompactLegalNotice = ({ className = "" }) => {
  return (
    <div className={`bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 ${className}`}>
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <Shield className="w-4 h-4 text-cyan-400 flex-shrink-0" />
        <span>
          <strong className="text-gray-300">PROPRIETARY:</strong> This content is confidential and protected. 
          Unauthorized use, reproduction, or distribution is strictly prohibited.
        </span>
      </div>
    </div>
  );
};

export default LegalFooter;
